import { zoneSplitTableName } from './zoneSplit';

/**
 * Registre des **tableaux paramétrables** — système « 1 variable + options ».
 *
 * Une entrée = une famille de table. `default` donne la config initiale,
 * `controls` décrit les axes **donnée** rendus génériquement par l'écran de
 * configuration (radio | select | switch | planning-style) et transformés en
 * query params par `buildTableQuery`. L'axe **présentation** (`columns[]` :
 * masquage, ordre, largeur, renommage) est commun à toutes les familles et
 * reste côté front.
 *
 * Module partagé entre le document et le site (cf.
 * `docs/webviewer/chantier-boxes-partagees.md`) : **pur**, aucune dépendance à
 * React, Mantine, Ant Design, au store ni à Puck. `src/constants/constants.js`
 * le réexporte pour que les appelants historiques ne bougent pas.
 */

// Config "donnée + présentation" des tableaux paramétrables (système « 1 variable
// + options »). Distinct de defaultTableConfiguration (style visuel du tableau).
// Stocké sur box.tableConfig. Axes donnée (via `controls` -> query params backend) ;
// axes présentation (columns: masquage/ordre/largeur) -> front, communs à tous.
//
// Registre déclaratif : ajouter une famille = 1 entrée ici + 1 service backend.
// `controls` décrit les contrôles data-axis rendus génériquement par le Drawer
// (type radio|select|switch). `option.capability` masque/désactive une option
// selon les capabilities renvoyées par /tables (ex. bpu additional_work).
// Formations : l'ancien interrupteur « Logo » (`logo: true`) remplaçait le titre
// de la formation par son logo, sous le libellé « Formation », en 1re position.
// Le tableau a désormais les deux colonnes : une ancienne config se relit dans
// ce modèle sans changer de rendu — le réglage de « Formation » passe à « Logo »
// et le titre est masqué. Sans réglage pour « Logo », il reste masqué.
const normalizeFormationConfig = (config) => {
  const { logo, ...rest } = config ?? {};
  const columns = Array.isArray(rest.columns) ? rest.columns : [];
  const find = (label) => columns.find((col) => col.label === label);
  if (find('Logo')) return { ...rest, columns };
  if (!(logo === true || logo === 'true')) {
    return { ...rest, columns: [...columns, { label: 'Logo', hidden: true }] };
  }
  // Ordre et largeurs de l'ancien tableau à logo : Logo, Poste, Description.
  const carry = (from, label, order, width) => {
    const old = find(from);
    return { ...old, label, hidden: !!old?.hidden, order: old?.order ?? order, width: old?.width ?? width };
  };
  return {
    ...rest,
    columns: [
      carry('Formation', 'Logo', 0, 20),
      carry('Poste', 'Poste', 1, 30),
      carry('Description', 'Description', 2, 50),
      { label: 'Formation', hidden: true, order: 3 },
    ],
  };
};

const PLANNING_ORIENTATION_CONTROL = {
  type: 'radio',
  field: 'orientation',
  labelKey: 'planning-orientation',
  options: [
    { value: 'horizontal', labelKey: 'planning-orientation-horizontal' },
    { value: 'vertical', labelKey: 'planning-orientation-vertical' },
  ],
};

// Style des screenshots planning. L'axe donnée est `styleDigest` (scalaire, donc
// comparable par tableConfigDataAxisChanged) ; `themeField` désigne la copie des
// couleurs réellement envoyée au backend. On fige ces couleurs sur la box plutôt
// que de ne garder qu'un id : une modification du style en admin ne doit pas
// changer l'apparence d'un document déjà composé (cf. bouton de mise à jour dans
// le Drawer).
const PLANNING_STYLE_CONTROL = {
  type: 'planning-style',
  field: 'styleDigest',
  themeField: 'theme',
  labelKey: 'planning-style',
};

const PLANNING_STYLE_DEFAULTS = (extra = {}) => ({
  ...extra,
  columns: [],
  styleId: null,
  styleName: null,
  styleDigest: null,
  theme: null,
});

// Aperçu des variantes de devis : la suite de lignes que chaque service émet,
// avec les libellés qu'il écrit (« Matériel », « Total matériel », « TOTAL HT »).
// Une forme courte par variante, à revoir seulement si un service change de
// structure. Le Drawer en fait de vraies lignes (cf. buildTablePreviewRows) :
// colonnes masquées, renommées et fusion auto s'y appliquent comme au document.
//
// Sortes de lignes d'une forme :
// - `item` : ligne de données, `line` : libellé et montant d'un élément
//   (coût annexe) ; ni fusion ni style, leur libellé change ;
// - `heading`, `subtotal`, `total` : libellé fixe, montant ou non ;
// - `fixed` : ligne de données au libellé fixe (« Total forfaitaire ») ; elle
//   se style mais ne fusionne pas, ses cases sont remplies ;
// - `zone` : type donné par l'API ; `title` : titre dont le libellé change
//   (un poste, une tenue), type déduit de la ligne (cf. tableRowKinds) ;
// - `empty` : ligne vide.
// `rowKind` + `match` : type déduit du libellé, pour une ligne fixe dont le
// libellé varie un peu (le taux de TVA). `fill` : colonnes remplies en plus du
// libellé (la photo d'une tenue).
const PREVIEW_ITEM = { kind: 'item', label: 'Article' };
const PREVIEW_UNPRICED_ITEM = { kind: 'item', label: 'Article', unpriced: true };
const PREVIEW_EMPTY = { kind: 'empty' };
// Une seule ligne de zone : sa fusion vaut pour toutes les zones (cf. rowKind).
const PREVIEW_ZONE = { kind: 'zone', label: 'Zone' };
// Le taux est dans le libellé (« TVA (5,5 %) ») : la ligne a son type.
const PREVIEW_VAT = (label) => ({ kind: 'total', label, rowKind: 'vat', match: /^TVA\b/ });
const PREVIEW_TOTALS = [
  { kind: 'total', label: 'TOTAL HT' },
  PREVIEW_VAT('TVA (20 %)'),
  { kind: 'total', label: 'TOTAL TTC' },
];
const PREVIEW_SUBTOTAL_BLOCKS = (item) => [
  { kind: 'heading', label: 'Matériel' },
  item,
  { kind: 'subtotal', label: 'Total matériel' },
  PREVIEW_EMPTY,
  { kind: 'heading', label: 'Prestation' },
  item,
  { kind: 'subtotal', label: 'Total prestation' },
  PREVIEW_EMPTY,
];
const DEVIS_PREVIEW_SHAPES = {
  detailed: [PREVIEW_ZONE, PREVIEW_ITEM, PREVIEW_EMPTY, ...PREVIEW_TOTALS],
  // Pas de ligne vide avant les totaux : le dernier bloc finit déjà par une.
  detailed_subtotals: [PREVIEW_ZONE, ...PREVIEW_SUBTOTAL_BLOCKS(PREVIEW_ITEM), ...PREVIEW_TOTALS],
  subtotals: [PREVIEW_ZONE, ...PREVIEW_SUBTOTAL_BLOCKS(PREVIEW_ITEM), ...PREVIEW_TOTALS],
  // Ce service n'écrit pas de montant sur la ligne de zone.
  total: [
    { ...PREVIEW_ZONE, unpriced: true },
    PREVIEW_ITEM, PREVIEW_EMPTY, ...PREVIEW_TOTALS,
  ],
  // Articles sans prix : seul le total de chaque zone est chiffré.
  total_zone: [
    PREVIEW_ZONE,
    PREVIEW_UNPRICED_ITEM,
    PREVIEW_EMPTY,
    ...PREVIEW_TOTALS,
  ],
};
const DEVIS_OPTION_PREVIEW_SHAPES = {
  ...DEVIS_PREVIEW_SHAPES,
  total_vat: DEVIS_PREVIEW_SHAPES.detailed,
  vat: DEVIS_PREVIEW_SHAPES.detailed,
};

// BPU et accord-cadre : un bloc par poste (ou profil), son titre puis taux,
// volume et total ; avec la consolidation, le même bloc pour l'ensemble.
const HOURLY_BLOCK = (title) => [
  title,
  { kind: 'fixed', label: 'Taux horaire' },
  { kind: 'fixed', label: 'Volume horaire' },
  { kind: 'fixed', label: 'Total forfaitaire' },
];
const HOURLY_SHAPE = (titleLabel, consolidation) => [
  ...HOURLY_BLOCK({ kind: 'title', label: titleLabel }),
  ...(consolidation ? HOURLY_BLOCK({ kind: 'heading', label: 'Consolidation' }) : []),
];

export const TABLE_CONFIG_DESCRIPTORS = {
  bpu: {
    default: { mode: 'profils', consolidation: true, variant: 'standard', columns: [] },
    labelColumn: 'Intitulé',
    // Prix seuls et travaux supplémentaires : une ligne par poste, sans bloc.
    previewShapes: {
      standard: (config) => HOURLY_SHAPE(config.mode === 'work_positions' ? 'Poste' : 'Profil', config.consolidation),
    },
    controls: [
      {
        type: 'radio',
        field: 'mode',
        // splitEntity : cet axe change l'ENTITÉ de découpe. `mode` bascule le
        // service délégué, donc les clés passent de rows_<profil_id> à
        // rows_<work_position_id>. Quand la découpe est imposée de l'extérieur
        // (section récursive bouclant sur des profils), l'envoyer ne renvoie
        // plus aucune slice correspondante -> tableau vidé. Cf.
        // tableConfigForSplitScope.
        splitEntity: true,
        labelKey: 'bpu-mode',
        options: [
          { value: 'profils', labelKey: 'bpu-mode-profils' },
          { value: 'work_positions', labelKey: 'bpu-mode-work-positions' },
        ],
      },
      {
        type: 'select',
        field: 'variant',
        labelKey: 'bpu-variant',
        options: [
          { value: 'standard', labelKey: 'bpu-variant-standard' },
          { value: 'only_price', labelKey: 'bpu-variant-only-price' },
          { value: 'additional_work', labelKey: 'bpu-variant-additional-work', capability: 'additional_work' },
        ],
      },
      { type: 'switch', field: 'consolidation', labelKey: 'bpu-consolidation', enabledWhenVariant: 'standard' },
    ],
  },
  fa: {
    default: { consolidation: true, columns: [] },
    labelColumn: 'Intitulé',
    previewShapes: { default: (config) => HOURLY_SHAPE('Poste', config.consolidation) },
    controls: [{ type: 'switch', field: 'consolidation', labelKey: 'fa-consolidation' }],
  },
  prestations: {
    default: { variant: 'standard', columns: [] },
    controls: [
      {
        type: 'select',
        field: 'variant',
        labelKey: 'prestations-variant',
        options: [
          { value: 'standard', labelKey: 'prestations-variant-standard' },
          { value: 'picture', labelKey: 'prestations-variant-picture' },
          { value: 'description', labelKey: 'prestations-variant-description' },
        ],
      },
    ],
  },
  ouvrages: {
    default: { details: false, columns: [] },
    controls: [{ type: 'switch', field: 'details', labelKey: 'ouvrages-details' }],
  },
  references: {
    default: { typology: false, source: 'both', columns: [] },
    controls: [
      { type: 'switch', field: 'typology', labelKey: 'references-typology' },
      {
        type: 'select',
        field: 'source',
        labelKey: 'references-source',
        options: [
          { value: 'both', labelKey: 'references-source-both' },
          { value: 'head_office', labelKey: 'source-head-office' },
          { value: 'entity', labelKey: 'source-entity' },
        ],
      },
    ],
  },
  work_contacts: {
    default: { type: 'client', columns: [] },
    controls: [
      {
        type: 'select',
        field: 'type',
        labelKey: 'contacts-type',
        options: [
          { value: 'client', labelKey: 'contacts-type-client' },
          { value: 'provided', labelKey: 'contacts-type-provided' },
          { value: 'decision_maker', labelKey: 'contacts-type-decision-maker' },
        ],
      },
    ],
  },
  turnover: {
    // Colonnes dynamiques (années propres à l'entité) -> édition de colonnes désactivée.
    default: { source: 'entity', repartition: false, columns: [] },
    columnsConfigurable: false,
    controls: [
      {
        type: 'select',
        field: 'source',
        labelKey: 'turnover-source',
        options: [
          { value: 'entity', labelKey: 'source-entity' },
          { value: 'head_office', labelKey: 'source-head-office' },
        ],
      },
      { type: 'switch', field: 'repartition', labelKey: 'turnover-repartition' },
    ],
  },
  outfits: {
    default: { variant: 'work_positions', columns: [] },
    labelColumn: 'Intitulé',
    // Articles détaillés : chaque tenue, avec sa photo, puis ses articles.
    previewShapes: {
      detailed_articles: [{ kind: 'title', label: 'Tenue', fill: ['Photo'] }, PREVIEW_ITEM, PREVIEW_EMPTY],
    },
    controls: [
      {
        type: 'select',
        field: 'variant',
        labelKey: 'outfits-variant',
        options: [
          { value: 'work_positions', labelKey: 'outfits-variant-work-positions' },
          { value: 'quantities', labelKey: 'outfits-variant-quantities' },
          { value: 'detailed_articles', labelKey: 'outfits-variant-detailed-articles' },
        ],
      },
    ],
  },
  dispositif: {
    default: { description: true, columns: [] },
    controls: [{ type: 'switch', field: 'description', labelKey: 'dispositif-description' }],
  },
  // Le logo est une colonne comme les autres, masquée par défaut (cf.
  // normalizeFormationConfig pour les configs de l'ancien interrupteur).
  formation: {
    default: { columns: [{ label: 'Logo', hidden: true }] },
    controls: [],
    normalizeConfig: (config) => normalizeFormationConfig(config),
  },
  dispositif_planning: {
    default: PLANNING_STYLE_DEFAULTS({ orientation: 'horizontal' }),
    controls: [PLANNING_ORIENTATION_CONTROL, PLANNING_STYLE_CONTROL],
  },
  dispositif_planning_profil: {
    default: PLANNING_STYLE_DEFAULTS({ orientation: 'horizontal' }),
    controls: [PLANNING_ORIENTATION_CONTROL, PLANNING_STYLE_CONTROL],
  },
  // Pas d'axe orientation : le vertical n'existe pas pour ce mécanisme (pas de
  // colonne d'attachement dédiée sur SpecialPlanning). Le style, lui, s'applique
  // comme aux autres plannings. `dates` : dates du planning sous son nom.
  dispositif_planning_special: {
    default: PLANNING_STYLE_DEFAULTS({ dates: false }),
    controls: [
      { type: 'switch', field: 'dates', labelKey: 'dispositif-planning-special-dates' },
      PLANNING_STYLE_CONTROL,
    ],
    // Une ligne par planning spécial, comme le backend : son poste, son nom et,
    // avec `dates`, ses dates dessous.
    previewSample: (config, n) => ({
      Poste: `Poste ${n}`,
      'Planning spécial': `Nom du planning ${n}${config.dates ? '\nJJ/MM/YYYY - JJ/MM/YYYY' : ''}`,
    }),
  },
  // `labelColumn` / `totalsColumn` : colonnes où le backend écrit les libellés
  // et les montants des lignes. Masquer la seconde vide les totaux : le Drawer
  // prévient. `previewShapes` : aperçu par variante (cf. plus haut).
  devis: {
    default: { variant: 'detailed', columns: [] },
    labelColumn: 'Intitulé',
    totalsColumn: 'Prix total',
    previewShapes: DEVIS_PREVIEW_SHAPES,
    controls: [
      {
        type: 'select',
        field: 'variant',
        labelKey: 'devis-variant',
        options: [
          { value: 'detailed', labelKey: 'devis-variant-detailed' },
          { value: 'detailed_subtotals', labelKey: 'devis-variant-detailed-subtotals' },
          { value: 'subtotals', labelKey: 'devis-variant-subtotals' },
          { value: 'total', labelKey: 'devis-variant-total' },
          { value: 'total_zone', labelKey: 'devis-variant-total-zone' },
        ],
      },
    ],
  },
  devis_option: {
    default: { variant: 'detailed', columns: [] },
    labelColumn: 'Intitulé',
    totalsColumn: 'Prix total HT',
    previewShapes: DEVIS_OPTION_PREVIEW_SHAPES,
    controls: [
      {
        type: 'select',
        field: 'variant',
        labelKey: 'devis-variant',
        options: [
          { value: 'detailed', labelKey: 'devis-variant-detailed' },
          { value: 'detailed_subtotals', labelKey: 'devis-variant-detailed-subtotals' },
          { value: 'subtotals', labelKey: 'devis-variant-subtotals' },
          { value: 'total', labelKey: 'devis-variant-total' },
          { value: 'total_zone', labelKey: 'devis-variant-total-zone' },
          { value: 'total_vat', labelKey: 'devis-option-variant-total-vat' },
          { value: 'vat', labelKey: 'devis-option-variant-vat' },
        ],
      },
    ],
  },
};

// Entrées « par zones » : même tableau, seule la question « un par zone » à la
// sélection les distingue (cf. Configurable::ZONE_ENTRIES côté back).
TABLE_CONFIG_DESCRIPTORS.devis_zones = TABLE_CONFIG_DESCRIPTORS.devis;
TABLE_CONFIG_DESCRIPTORS.devis_option_zones = TABLE_CONFIG_DESCRIPTORS.devis_option;

// Aperçu de tableaux historiques, sans variante ni axe donnée : ils ne sont pas
// « paramétrables », seule leur structure de lignes est décrite.
// `previewColumns` : leurs colonnes, que l'API ne donne qu'avec des lignes.
// Elles servent tant que le tableau est vide (dans un modèle).
const TABLE_PREVIEW_DESCRIPTORS = {
  prices: {
    labelColumn: 'Intitulé',
    totalsColumn: 'Prix de vente total',
    previewColumns: ['Intitulé', 'Prix de vente unitaire', 'Quantité', 'Prix de vente total'],
    previewShapes: {
      default: [
        { kind: 'heading', label: 'MASSE SALARIALE' },
        { kind: 'item', label: 'Poste' },
        { kind: 'heading', label: 'COÛTS ANNEXES' },
        { kind: 'line', label: 'Coût annexe' },
        PREVIEW_EMPTY,
        { kind: 'total', label: 'TOTAL HT' },
        PREVIEW_VAT('TVA (20%)'),
        { kind: 'total', label: 'TOTAL TTC' },
      ],
    },
  },
  workforce_sizing: {
    labelColumn: 'Profils',
    previewColumns: ['Profils', 'Heures', 'Effectif théorique', 'Effectif remplaçant disponible',
      'Renforts formés', 'Coefficient conventionnel'],
    previewShapes: { default: [{ kind: 'item', label: 'Profil' }, { kind: 'fixed', label: 'Total' }] },
  },
  // Agents par période puis par poste : deux niveaux de titre.
  agents_guarding: {
    labelColumn: '',
    previewColumns: ['', 'Primes', 'Indemnités'],
    previewShapes: {
      default: [
        { kind: 'title', label: 'Période' },
        { kind: 'title', label: 'Poste' },
        { kind: 'item', label: 'Agent' },
      ],
    },
  },
  // Le total est chiffré dans chaque colonne : il se style, ne fusionne pas.
  prices_global_cleaning: {
    labelColumn: '',
    previewColumns: ['', 'Prix de vente HT', 'Forfait mensuel'],
    previewShapes: {
      default: [
        { kind: 'item', label: 'Prestations' },
        { kind: 'item', label: 'Matériels' },
        PREVIEW_EMPTY,
        { kind: 'fixed', label: 'TOTAL HT' },
      ],
    },
  },
  // Lignes de données seules : des colonnes, pas de forme.
  profils: { previewColumns: ['Intitulé du profil', 'Postes concernés', 'Détail'] },
  workpositions_formations: { previewColumns: ['Poste', 'Statut', 'Formations', 'Compétences'] },
  // Une colonne par formation, nommée comme elle : seule la première est fixe.
  formations_work_positions: { previewColumns: ['Poste / Formation'] },
  materials: { previewColumns: ['Photo', 'Intitulé', 'Description', 'Quantité'] },
  materials_list: { previewColumns: ['Intitulé', 'Quantité'] },
  work_positions: { previewColumns: ['Poste', 'Planning', 'Description', 'Volume horaire'] },
  work_positions_without_description: { previewColumns: ['Poste', 'Planning', 'Volume horaire'] },
  work_positions_plannings: { previewColumns: ['Poste', 'Planning'] },
  outfits_work_positions: { previewColumns: ['Type de tenue', 'Photo', 'Postes', 'Description'] },
  outfits_work_positions_quantities: { previewColumns: ['Photo', 'Description', 'Postes'] },
  outfits_detailed_articles: {
    previewColumns: ['Intitulé', 'Photo', 'Quantité dotation initiale', 'Quantité renouvellement'],
  },
  profils_by_zone: { previewColumns: ['Zone', 'Intitulé du profil', 'Postes concernés', 'Détail'] },
  formations: { previewColumns: ['Poste', 'Formation', 'Description'] },
  formations_logos: { previewColumns: ['Formation', 'Poste', 'Description'] },
  prestations_picture_work: { previewColumns: ['Photo', 'Intitulé', 'Description'] },
  prestations_option_picture_work: { previewColumns: ['Photo', 'Intitulé', 'Description'] },
  prestations_description_work: { previewColumns: ['Intitulé', 'Description'] },
  prestations_option_description_work: { previewColumns: ['Intitulé', 'Description'] },
  material_cleanings: { previewColumns: ['Image', 'Intitulé', 'Description'] },
  outfit_cleanings: { previewColumns: ['Image', 'Intitulé', 'Description'] },
  carrycot_cleanings: { previewColumns: ['Image', 'Intitulé', 'Description'] },
  movement_cleanings: { previewColumns: ['Image', 'Intitulé', 'Description'] },
  formation_cleanings: { previewColumns: ['Logo', 'Intitulé', 'Description'] },
  // Une colonne par jour ; « M » puis « M  » : mardi et mercredi.
  prestation_cleanings: {
    previewColumns: ['Type de local', 'Intitulé', 'Qté', 'Profils', 'L', 'M', 'M ', 'J', 'V', 'S', 'D', 'JF'],
  },
  // Années de l'entité : « N-1 »… quand elles n'ont pas de libellé.
  entity_turnover_work: { previewColumns: ['N-1', 'N-2', 'N-3'] },
  entity_turnover_repartition_work: { previewColumns: ['', 'N-1', 'N-2', 'N-3'] },
  head_office_turnover_work: { previewColumns: ['N-1', 'N-2', 'N-3'] },
  head_office_turnover_repartition_work: { previewColumns: ['', 'N-1', 'N-2', 'N-3'] },
  // Tableaux d'une offre. « Contacts » y est une liste de questions : le
  // tableau d'opportunité du même nom n'est plus proposé.
  contacts: { previewColumns: ['Intitulé', 'Réponse'] },
  infos: { previewColumns: ['Intitulé', 'Réponse'] },
  market: { previewColumns: ['Intitulé', 'Réponse'] },
  procedure: { previewColumns: ['Intitulé', 'Réponse'] },
  results: { previewColumns: ['Intitulé', 'Réponse'] },
  risk: { previewColumns: ['Intitulé', 'Réponse'] },
  subcontractors: { previewColumns: ['N° lot', 'Informations administratives', 'Prestations exécutées'] },
  cocontractors: {
    previewColumns: ['N° lot', 'Informations administratives', 'Prestations exécutées', 'Identification du Mandataire'],
  },
  opportunities_financial: { previewColumns: ['Intitulé', 'Coût de revient', 'Marge', 'Prix de vente (HT)'] },
  head_office_turnover: { previewColumns: ['', 'N-1', 'N-2', 'N-3'] },
  head_office_turnover_repartition: { previewColumns: ['', 'N-1', 'N-2', 'N-3'] },
  entity_in_charge_turnover: { previewColumns: ['', 'N-1', 'N-2', 'N-3'] },
  entity_in_charge_turnover_repartition: { previewColumns: ['', 'N-1', 'N-2', 'N-3'] },
};

const CONTACT_COLUMNS = ['Prénom Nom', 'Fonction', 'Téléphone', 'Email'];
['contacts_work', 'client_contacts_work', 'provided_customer_contacts_work',
  'decision_maker_customer_contacts_work'].forEach((name) => {
  TABLE_PREVIEW_DESCRIPTORS[name] = { previewColumns: CONTACT_COLUMNS };
});
const REFERENCE_COLUMNS = ['Photo', 'Intitulé', 'Description de la mission', 'Période', "Chiffre d'affaires",
  'Agences concernées', 'Typologie du site'];
['references_global', 'references_global_head_office', 'references_global_entity', 'references_global_typology',
  'references_typology_head_office', 'references_typology_entity'].forEach((name) => {
  TABLE_PREVIEW_DESCRIPTORS[name] = { previewColumns: REFERENCE_COLUMNS };
});
['ouvrages_work', 'ouvrages_details_work', 'ouvrages_option_work', 'ouvrages_option_details_work'].forEach((name) => {
  TABLE_PREVIEW_DESCRIPTORS[name] = { previewColumns: ['Photo', 'Intitulé', 'Description', 'Quantité'] };
});

// Produits « par zones » (matériels, prestations… cf. zoneSplit) : chaque zone
// ouvre sur sa ligne de titre, que l'API type `zone`.
const zoneProductsPreview = (previewColumns) => ({
  labelColumn: 'Intitulé',
  previewColumns,
  previewShapes: { default: [PREVIEW_ZONE, PREVIEW_ITEM] },
});
const ZONE_PRODUCTS = {
  materials: ['Photo', 'Intitulé', 'Référence', 'Quantité', 'Marque'],
  fonctionnalities: ['Photo', 'Intitulé', 'Description', 'Quantité'],
  prestations: ['Photo', 'Intitulé', 'Référence', 'Quantité'],
  all_articles_without_prices: ['Photo', 'Intitulé', 'Référence', 'Quantité', 'Marque'],
};
Object.entries(ZONE_PRODUCTS).forEach(([family, columns]) => {
  ['zones_work', 'simple_zones', 'elec_zones'].forEach((suffix) => {
    TABLE_PREVIEW_DESCRIPTORS[`${family}_${suffix}`] = zoneProductsPreview(columns);
  });
});
['materials', 'fonctionnalities', 'prestations'].forEach((family) => {
  TABLE_PREVIEW_DESCRIPTORS[`${family}_option_zones_work`] = zoneProductsPreview(ZONE_PRODUCTS[family]);
});
TABLE_PREVIEW_DESCRIPTORS.all_articles_option_without_prices_zones_work =
  zoneProductsPreview(ZONE_PRODUCTS.all_articles_without_prices);
// Les mêmes, non découpés : leurs colonnes, sans ligne de zone. Le service
// d'une section récursive n'a pas de slice sans article : le tableau du
// document reste alors vide, comme dans un modèle.
Object.entries(ZONE_PRODUCTS).forEach(([family, previewColumns]) => {
  ['work', 'simple', 'elec'].forEach((suffix) => {
    TABLE_PREVIEW_DESCRIPTORS[`${family}_${suffix}`] = { previewColumns };
  });
});
['materials', 'fonctionnalities', 'prestations'].forEach((family) => {
  TABLE_PREVIEW_DESCRIPTORS[`${family}_option_work`] = { previewColumns: ZONE_PRODUCTS[family] };
});
TABLE_PREVIEW_DESCRIPTORS.all_articles_option_without_prices_work = {
  previewColumns: ZONE_PRODUCTS.all_articles_without_prices,
};

// Anciens noms d'un tableau devenu paramétrable : même service que l'une de ses
// variantes, donc même forme et mêmes colonnes. Les configs de certaines
// entreprises les listent encore.
const LEGACY_TABLE_ALIASES = {
  prices_work: ['devis', { variant: 'detailed' }],
  prices_simple: ['devis', { variant: 'detailed' }],
  prices_elec: ['devis', { variant: 'detailed' }],
  detailed_prices_work: ['devis', { variant: 'detailed_subtotals' }],
  detailed_prices_simple: ['devis', { variant: 'detailed_subtotals' }],
  detailed_prices_elec: ['devis', { variant: 'detailed_subtotals' }],
  detailed_prices_subtotal_prices_only_work: ['devis', { variant: 'subtotals' }],
  detailed_prices_only_total_price_work: ['devis', { variant: 'total' }],
  detailed_prices_only_total_and_zone_price_work: ['devis', { variant: 'total_zone' }],
  prices_option_work: ['devis_option', { variant: 'detailed' }],
  detailed_prices_option_work: ['devis_option', { variant: 'detailed_subtotals' }],
  detailed_prices_option_subtotal_prices_only_work: ['devis_option', { variant: 'subtotals' }],
  detailed_prices_option_only_total_price_work: ['devis_option', { variant: 'total' }],
  detailed_prices_option_only_total_and_zone_price_work: ['devis_option', { variant: 'total_zone' }],
  prices_option_total_work: ['devis_option', { variant: 'total_vat' }],
  prices_option_vat_work: ['devis_option', { variant: 'vat' }],
  bpu_profils: ['bpu', { mode: 'profils', variant: 'standard', consolidation: true }],
  bpu_profils_without_consolidation: ['bpu', { mode: 'profils', variant: 'standard', consolidation: false }],
  bpu_work_positions: ['bpu', { mode: 'work_positions', variant: 'standard', consolidation: true }],
  bpu_work_positions_without_consolidation: [
    'bpu', { mode: 'work_positions', variant: 'standard', consolidation: false },
  ],
  bpu_profils_only_price: ['bpu', { mode: 'profils', variant: 'only_price' }],
  bpu_profils_additional_work: ['bpu', { mode: 'profils', variant: 'additional_work' }],
  bpu_work_positions_only_price: ['bpu', { mode: 'work_positions', variant: 'only_price' }],
  bpu_work_positions_additional_work: ['bpu', { mode: 'work_positions', variant: 'additional_work' }],
  fa_work_positions: ['fa', { consolidation: true }],
  fa_work_positions_without_consolidation: ['fa', { consolidation: false }],
  work_positions_plannings_vertical: ['dispositif_planning', { orientation: 'vertical' }],
  work_positions_plannings_horizontal: ['dispositif_planning', { orientation: 'horizontal' }],
  work_positions_profil_plannings_vertical: ['dispositif_planning_profil', { orientation: 'vertical' }],
  work_positions_profil_plannings_horizontal: ['dispositif_planning_profil', { orientation: 'horizontal' }],
  // Le vertical sert l'horizontal (pas de vertical pour ce mécanisme).
  work_positions_special_plannings_vertical: ['dispositif_planning_special', {}],
  work_positions_special_plannings_horizontal: ['dispositif_planning_special', {}],
};

/**
 * Tableau paramétrable dont `tableName` est un ancien nom, avec la config de
 * la variante qu'il désigne : `{ tableName, tableConfig }`, ou null.
 */
export const getLegacyTableAlias = (tableName) => {
  const alias = LEGACY_TABLE_ALIASES[(tableName || '').toLowerCase()];
  return alias ? { tableName: alias[0], tableConfig: alias[1] } : null;
};

const FIXED_LABEL_KINDS = ['heading', 'subtotal', 'total', 'fixed'];

/**
 * Ce que le document sait des lignes d'un tableau, tiré de sa forme :
 * - `fixedLabels` : libellés fixes, qui se règlent par libellé même sur une
 *   ligne de données (« Total forfaitaire ») ;
 * - `title` : ses titres de libellé variable prennent le type `title`, sauf un
 *   libellé fixe ; `titleIgnore` : colonnes remplies d'un titre (la photo) ;
 * - `kindPatterns` : type déduit du libellé (`{ rowKind, match }`).
 */
const rowStructureOf = (shape) => ({
  fixedLabels: shape.filter((row) => FIXED_LABEL_KINDS.includes(row.kind) && !row.match).map((row) => row.label),
  title: shape.some((row) => row.kind === 'title'),
  titleIgnore: shape.filter((row) => row.kind === 'title').flatMap((row) => row.fill ?? []),
  kindPatterns: shape.filter((row) => row.match).map(({ rowKind, match }) => ({ rowKind, match })),
});

export const CONFIGURABLE_TABLE_NAMES = Object.keys(TABLE_CONFIG_DESCRIPTORS);

export const isConfigurableTable = (tableName) =>
  CONFIGURABLE_TABLE_NAMES.includes((tableName || '').toLowerCase());

export const getTableConfigControls = (tableName) =>
  TABLE_CONFIG_DESCRIPTORS[(tableName || '').toLowerCase()]?.controls || [];

// Colonnes éditables dans le Drawer ? (false = colonnes dynamiques, ex. C.A.
// dont les libellés d'années varient par entité -> pas d'override par label).
export const isTableColumnsConfigurable = (tableName) =>
  TABLE_CONFIG_DESCRIPTORS[(tableName || '').toLowerCase()]?.columnsConfigurable !== false;

// Libellés des axes DONNÉE qui s'écartent du défaut, pour enrichir le titre du
// tableau (ex. « BPU — Par postes · Prix de vente horaire »). Seuls les axes
// radio/select portent un libellé par option : on saute les switches (pas de nom
// court non ambigu) et les axes de style planning. Un axe resté au défaut n'apparaît
// pas -> titre nu tant que rien n'est modifié. Retourne des labelKeys (le composant
// applique `t`, constants n'a pas accès à i18n).
export const getTableConfigLabelKeys = (tableName, tableConfig) => {
  if (!tableConfig) return [];
  const descriptor = TABLE_CONFIG_DESCRIPTORS[(tableName || '').toLowerCase()];
  if (!descriptor) return [];
  return descriptor.controls.flatMap((control) => {
    if (control.type !== 'radio' && control.type !== 'select') return [];
    const value = tableConfig[control.field];
    if (value === undefined || value === descriptor.default[control.field]) return [];
    const option = control.options.find((o) => o.value === value);
    return option ? [option.labelKey] : [];
  });
};

// Titre d'un tableau branché tel qu'un modèle l'affiche à la place de ses
// cellules : nom traduit, puis les options DONNÉE choisies. `t` vient de
// l'appelant (module pur, sans i18n).
export const tableTitleWithOptions = (tableName, tableConfig, t) => {
  const base = t(tableName);
  const keys = getTableConfigLabelKeys(tableName, tableConfig);
  return keys.length ? `${base} — ${keys.map((k) => t(k)).join(' · ')}` : base;
};

// La table se découpe-t-elle par entité (une slice `rows_<id>` par zone,
// profil…) ? `splittableTables` vient de la liste des tables du document ; le
// backend y met des noms ou des objets `{ tableName | table_name }`.
export const isSplittableTable = (splittableTables, tableName) =>
  Array.isArray(splittableTables) &&
  splittableTables.some((entry) => {
    if (typeof entry === 'string') return entry === tableName;
    return entry?.tableName === tableName || entry?.table_name === tableName;
  });

// Aperçu structurel de la table pour cette config : { shape, labelColumn,
// totalsColumn, structure }, ou null si la table n'en déclare pas (aperçu
// générique). Une forme peut dépendre de la config (la consolidation du BPU).
export const getTablePreviewLayout = (tableName, tableConfig) => {
  const name = (tableName || '').toLowerCase();
  const alias = getLegacyTableAlias(name);
  if (alias) return getTablePreviewLayout(alias.tableName, { ...tableConfig, ...alias.tableConfig });
  const legacy = TABLE_PREVIEW_DESCRIPTORS[name];
  const descriptor = TABLE_CONFIG_DESCRIPTORS[name] ?? legacy;
  if (!descriptor?.previewShapes) return null;
  const config = { ...descriptor.default, ...tableConfig };
  // Un historique n'a qu'une forme, quelle que soit la variante de la box
  // (Prestations découpé par zone garde la sienne).
  const entry = descriptor.previewShapes[legacy ? 'default' : (config.variant ?? 'default')];
  const shape = typeof entry === 'function' ? entry(config) : entry;
  if (!shape) return null;
  return {
    shape,
    labelColumn: descriptor.labelColumn,
    totalsColumn: descriptor.totalsColumn,
    previewColumns: descriptor.previewColumns ?? null,
    structure: rowStructureOf(shape),
  };
};

// Cases d'exemple de la ligne n (1, 2…) de l'aperçu générique, par libellé de
// colonne, ou null : les autres cases gardent « Ligne n » / « — ».
export const getTablePreviewSample = (tableName, tableConfig) => {
  const descriptor = TABLE_CONFIG_DESCRIPTORS[(tableName || '').toLowerCase()];
  if (!descriptor?.previewSample) return null;
  const config = { ...descriptor.default, ...tableConfig };
  return (n) => descriptor.previewSample(config, n);
};

// Colonnes d'exemple d'un historique, au format du catalogue ({ label, width }) :
// sans ligne (modèle), l'API ne dit pas ses colonnes. Découpé par zone, il prend
// celles de sa version « par zones ».
// Lues sans passer par la forme : un tableau sans forme peut en déclarer.
export const previewCatalogColumns = (tableName, tableConfig, { split = false } = {}) => {
  const name = (split ? zoneSplitTableName(tableName) : tableName || '').toLowerCase();
  const previewColumns =
    getTablePreviewLayout(name, tableConfig)?.previewColumns ?? TABLE_PREVIEW_DESCRIPTORS[name]?.previewColumns;
  return (previewColumns ?? []).map((label) => ({
    label,
    width: Number((100 / previewColumns.length).toFixed(2)),
  }));
};

// Structure des lignes du tableau (cf. rowStructureOf), ou null : sans forme
// déclarée, ses lignes ne fusionnent pas et ne se stylent pas. Un planning, dont
// l'image n'est pas dans la case, passerait sinon pour une ligne-titre.
export const getTableRowStructure = (tableName, tableConfig) =>
  getTablePreviewLayout(tableName, tableConfig)?.structure ?? null;

/**
 * Config d'une box relue dans le modèle courant de sa famille (ancienne option
 * remplacée par une colonne, par ex.). Sans règle de relecture, la config telle
 * quelle. À appliquer à toute config lue sur une box avant de s'en servir.
 */
export const resolveTableConfig = (tableName, tableConfig) => {
  const descriptor = TABLE_CONFIG_DESCRIPTORS[(tableName || '').toLowerCase()];
  return descriptor?.normalizeConfig ? descriptor.normalizeConfig(tableConfig) : tableConfig;
};

export const defaultTableConfig = (tableName) => {
  const descriptor = TABLE_CONFIG_DESCRIPTORS[(tableName || '').toLowerCase()];
  // Overrides de présentation par colonne { label, hidden, order, width }.
  // Vide = toutes les colonnes du catalogue, ordre/largeur d'origine.
  return descriptor ? JSON.parse(JSON.stringify(descriptor.default)) : null;
};

// Un axe DONNÉE a-t-il changé entre deux configs ? (mode/consolidation/variante/
// orientation… = champs des `controls`). true -> le backend renvoie d'autres rows,
// refetch obligatoire. false -> seul `columns` (présentation) a bougé -> réappli
// locale possible, sans requête. Prev absent = première config -> considéré changé.
export const tableConfigDataAxisChanged = (tableName, prevConfig, nextConfig) => {
  const controls = getTableConfigControls(tableName);
  // Première config d'un tableau historique : il n'a aucun axe donnée, seule
  // la présentation peut changer.
  if (!prevConfig) return controls.length > 0;
  return controls.some(
    (control) => prevConfig[control.field] !== nextConfig[control.field]
  );
};

// Axes DONNÉE transmissibles quand la découpe des lignes est imposée de
// l'extérieur (box dans une section récursive : une slice rows_<row._id> par
// itération). Tout part SAUF les axes marqués `splitEntity`, qui changeraient
// l'entité de découpe et feraient disparaître la slice attendue.
// Retourne uniquement les axes (pas `columns`, purement présentation et appliqué
// côté client) -> sérialisable tel quel comme clé de cache.
export const tableConfigForSplitScope = (tableName, tableConfig) => {
  if (!tableConfig) return null;
  const scoped = {};
  getTableConfigControls(tableName).forEach((control) => {
    if (control.splitEntity) return;
    const field = control.themeField || control.field;
    if (tableConfig[field] !== undefined) scoped[field] = tableConfig[field];
  });
  return Object.keys(scoped).length ? scoped : null;
};
